/**
 * 星球地图的公开居民图层：GET /public/world/residents（契约 PublicResidentMap，W2-R）。纯读：不推进世界、不调模型、不写库。
 *
 * 为什么先放在模块里、不进 shared/services 的 PetsService：shared 这一层要报主窗口批了才动（照 communicator/petFriends.service.ts 的做法）。
 * 批下后把 page() 搬进 PetsService（live / 演示两套搬进 pets 模块的 services），页面只改取读口的那一行（见 ./usePublicMap）。
 *
 * 分页：一页最多 100 位（服务端 MAP_PAGE_MAX），next_cursor 原样带回；按 pet_id 去重合并（翻页期间名册变动，同一位可能出现在两页）。
 * 某一页读不出来：前面已经读到的照常给出，但标明“不完整”（complete=false），页面不能把这一部分说成全星球；第一页就读不出来才算整体失败。
 */
import type { PublicMapResident, PublicResidentMap } from "@/shared/contracts";
import type { ApiClient } from "@/shared/api/client";

export interface PublicMapReader {
  /** 一页公开居民；cursor 为 null 取第一页。 */
  page(cursor: string | null, signal?: AbortSignal): Promise<PublicResidentMap>;
}

export function livePublicMapReader(api: ApiClient): PublicMapReader {
  return {
    page: (cursor, signal) => api.request<PublicResidentMap>("/public/world/residents", { query: { cursor }, signal }),
  };
}

/** 演示模式没有公开位置：给一份空图层（如实说“地图上暂时看不到”），不编位置。 */
export function demoPublicMapReader(): PublicMapReader {
  return {
    page: async () => ({ server_time: new Date().toISOString(), coord_system: "wgs84", residents: [], total: 0, next_cursor: null, cache_seconds: 30, grid_deg: 0.01, nearby_radius_m: 3000 }),
  };
}

/** 翻页上限：100 × 30 = 3000 位，远超现在的名册；防服务端游标出错时无限翻。 */
export const MAX_PAGES = 30;

export interface PublicMapLayer {
  /** 去重合并后的居民（按读到的先后） */
  residents: PublicMapResident[];
  /** 服务端说的图层总人数（取最后一次成功读到的那页的 total） */
  total: number;
  /** 所有页都读到了 */
  complete: boolean;
  /** 没读完时的那个错误（第几页之后读不出来、或游标重复、或到了翻页上限） */
  partialError: unknown;
  server_time: string;
  /** 坐标系（PublicResidentMap.coord_system，星球组件要）；缺省按 wgs84 */
  coord_system: string;
  grid_deg: number;
}

export async function loadPublicMap(reader: PublicMapReader, signal?: AbortSignal): Promise<PublicMapLayer> {
  const seen = new Set<string>();
  const cursors = new Set<string>();
  const residents: PublicMapResident[] = [];
  // 第一页读不出来：整体失败，交给页面的“读不出来、重试”
  let page = await reader.page(null, signal);
  let total = page.total;
  const first = page;
  const add = (items: PublicMapResident[]) => {
    for (const item of items) {
      if (seen.has(item.pet_id)) continue;
      seen.add(item.pet_id);
      residents.push(item);
    }
  };
  add(page.residents);
  let pages = 1;
  let partialError: unknown = null;
  while (page.next_cursor) {
    if (cursors.has(page.next_cursor) || pages >= MAX_PAGES) {
      partialError = new Error(cursors.has(page.next_cursor) ? "cursor_repeated" : "too_many_pages");
      break;
    }
    cursors.add(page.next_cursor);
    try {
      page = await reader.page(page.next_cursor, signal);
    } catch (error) {
      if (signal?.aborted) throw error;
      partialError = error;
      break;
    }
    pages += 1;
    total = page.total;
    add(page.residents);
  }
  return { residents, total, complete: partialError === null, partialError, server_time: first.server_time, coord_system: first.coord_system || "wgs84", grid_deg: first.grid_deg };
}
